<template>
  <Teleport to="body">
    <div v-if="show" class="pc-drp">
      <div class="pc-drp__mask" @click="handleCancel" />
      <div
        ref="panelRef"
        class="pc-drp__panel"
        :style="panelStyle"
        @click.stop
      >
        <div class="pc-drp__arrow" aria-hidden="true" />
        <div class="pc-drp__months">
          <div class="pc-drp__col">
            <div class="pc-drp__head pc-drp__head--left">
              <button type="button" class="pc-drp__nav" @click="shiftLeftYear(-1)">«</button>
              <button type="button" class="pc-drp__nav" @click="shiftLeftMonth(-1)">‹</button>
              <span class="pc-drp__title">{{ titleLeft }}</span>
            </div>
            <div class="pc-drp__weeks">
              <span v-for="w in weekLabels" :key="w" class="pc-drp__weekday">{{ w }}</span>
            </div>
            <div class="pc-drp__grid">
              <button
                v-for="(cell, idx) in cellsLeft"
                :key="'L' + idx"
                type="button"
                class="pc-drp__day"
                :class="dayClass(cell)"
                :disabled="cell.disabled"
                @click="onPick(cell)"
              >
                {{ cell.dayjs.date() }}
              </button>
            </div>
          </div>
          <div class="pc-drp__divider" />
          <div class="pc-drp__col">
            <div class="pc-drp__head pc-drp__head--right">
              <span class="pc-drp__title">{{ titleRight }}</span>
              <button type="button" class="pc-drp__nav" @click="shiftLeftMonth(1)">›</button>
              <button type="button" class="pc-drp__nav" @click="shiftLeftYear(1)">»</button>
            </div>
            <div class="pc-drp__weeks">
              <span v-for="w in weekLabels" :key="'r' + w" class="pc-drp__weekday">{{ w }}</span>
            </div>
            <div class="pc-drp__grid">
              <button
                v-for="(cell, idx) in cellsRight"
                :key="'R' + idx"
                type="button"
                class="pc-drp__day"
                :class="dayClass(cell)"
                :disabled="cell.disabled"
                @click="onPick(cell)"
              >
                {{ cell.dayjs.date() }}
              </button>
            </div>
          </div>
        </div>
        <div class="pc-drp__footer">
          <button type="button" class="pc-drp__btn pc-drp__btn--ghost" @click="handleCancel">取消</button>
          <button
            type="button"
            class="pc-drp__btn pc-drp__btn--primary"
            :disabled="!canConfirm"
            @click="handleConfirm"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import dayjs from 'dayjs'
import { formatString } from '@/components/dateFilterPanel/utils.js'

const props = defineProps({
  show: { type: Boolean, default: false },
  /** 触发框 getBoundingClientRect()，用于贴近输入框定位 */
  anchorRect: { type: Object, default: null },
  start: { type: String, default: '' },
  end: { type: String, default: '' },
  minDate: { type: Date, default: null },
  maxDate: { type: Date, default: null },
})

const emit = defineEmits(['update:show', 'confirm', 'cancel'])

const panelRef = ref(null)
const panelStyle = ref({})
const leftMonth = ref(dayjs().startOf('month'))
const pickStart = ref('')
const pickEnd = ref('')

const weekLabels = ['日', '一', '二', '三', '四', '五', '六']

const minD = computed(() => (props.minDate ? dayjs(props.minDate) : dayjs().subtract(2, 'year')))
const maxD = computed(() => (props.maxDate ? dayjs(props.maxDate) : dayjs().add(1, 'year')))

const titleLeft = computed(() => {
  const m = leftMonth.value
  return `${m.year()} 年 ${m.month() + 1} 月`
})
const titleRight = computed(() => {
  const m = leftMonth.value.add(1, 'month')
  return `${m.year()} 年 ${m.month() + 1} 月`
})

function buildCells(monthBase) {
  const start = monthBase.startOf('month')
  const end = monthBase.endOf('month')
  const pad = start.day()
  const daysInMonth = end.date()
  const cells = []
  for (let i = pad - 1; i >= 0; i--) {
    const d = start.subtract(i + 1, 'day')
    cells.push({ dayjs: d, inMonth: false, disabled: isDisabled(d) })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = monthBase.date(d)
    cells.push({ dayjs: dt, inMonth: true, disabled: isDisabled(dt) })
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].dayjs
    const n = last.add(1, 'day')
    cells.push({ dayjs: n, inMonth: false, disabled: isDisabled(n) })
  }
  while (cells.length < 42) {
    const last = cells[cells.length - 1].dayjs
    const n = last.add(1, 'day')
    cells.push({ dayjs: n, inMonth: false, disabled: isDisabled(n) })
  }
  return cells.slice(0, 42)
}

function isDisabled(d) {
  if (d.isBefore(minD.value, 'day') || d.isAfter(maxD.value, 'day')) return true
  return false
}

const cellsLeft = computed(() => buildCells(leftMonth.value))
const cellsRight = computed(() => buildCells(leftMonth.value.add(1, 'month')))

function shiftLeftMonth(delta) {
  leftMonth.value = leftMonth.value.add(delta, 'month')
}

function shiftLeftYear(delta) {
  leftMonth.value = leftMonth.value.add(delta, 'year')
}

function syncPanelPosition() {
  const run = () => {
    if (!props.show || !props.anchorRect || !panelRef.value) {
      return
    }
    const a = props.anchorRect
    const el = panelRef.value
    const w = el.offsetWidth || 560
    const h = el.offsetHeight || 400
    const vw = window.innerWidth
    const vh = window.innerHeight
    const gap = 8

    // 面板左缘与输入框左缘对齐（偏右时贴右屏）
    let left = a.left
    if (left + w > vw - 12) {
      left = Math.max(12, vw - w - 12)
    }
    if (left < 12) left = 12

    // 始终放在时间选择输入框正下方，不翻到输入框上方
    const top = a.bottom + gap

    const spaceBelow = vh - top - 12
    const style = {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
    }

    if (h > spaceBelow && spaceBelow > 140) {
      style.maxHeight = `${spaceBelow}px`
      style.overflowY = 'auto'
    } else {
      style.maxHeight = ''
      style.overflowY = ''
    }

    // 小三角对准输入框水平中心（相对面板）
    const anchorCx = a.left + a.width / 2
    let arrowCenterPx = anchorCx - left
    arrowCenterPx = Math.max(18, Math.min(arrowCenterPx, w - 18))
    style['--pc-drp-arrow-center-x'] = `${arrowCenterPx}px`

    panelStyle.value = style
  }

  nextTick(() => {
    requestAnimationFrame(() => {
      run()
      requestAnimationFrame(run)
    })
  })
}

watch(
  () => props.show,
  (v) => {
    if (v) {
      const s = props.start && dayjs(props.start).isValid() ? props.start : dayjs().format(formatString)
      const e = props.end && dayjs(props.end).isValid() ? props.end : s
      pickStart.value = s
      pickEnd.value = e
      leftMonth.value = dayjs(s).startOf('month')
      syncPanelPosition()
    }
  }
)

watch(
  () => [props.anchorRect, props.show],
  () => {
    if (props.show) syncPanelPosition()
  },
  { deep: true }
)

const canConfirm = computed(() => {
  if (!pickStart.value) return false
  if (!pickEnd.value) return true
  return (
    dayjs(pickEnd.value).isSame(pickStart.value, 'day') ||
    dayjs(pickEnd.value).isAfter(pickStart.value, 'day')
  )
})

function inRangeStr(ds) {
  if (!pickStart.value) return false
  const end = pickEnd.value || pickStart.value
  const a = dayjs(pickStart.value)
  const b = dayjs(end)
  const lo = a.isBefore(b, 'day') ? a : b
  const hi = a.isBefore(b, 'day') ? b : a
  const d = dayjs(ds)
  return (
    (d.isAfter(lo, 'day') || d.isSame(lo, 'day')) &&
    (d.isBefore(hi, 'day') || d.isSame(hi, 'day'))
  )
}

function dayClass(cell) {
  const ds = cell.dayjs.format(formatString)
  const classes = {
    'is-out': !cell.inMonth,
    'is-today': cell.dayjs.isSame(dayjs(), 'day'),
    'is-start': pickStart.value && ds === pickStart.value,
    'is-end': pickEnd.value && ds === pickEnd.value,
    'in-range': cell.inMonth && pickStart.value && inRangeStr(ds),
  }
  return classes
}

function onPick(cell) {
  if (cell.disabled) return
  const ds = cell.dayjs.format(formatString)
  if (!pickStart.value || (pickStart.value && pickEnd.value)) {
    pickStart.value = ds
    pickEnd.value = ''
    return
  }
  if (dayjs(ds).isBefore(pickStart.value, 'day')) {
    pickEnd.value = pickStart.value
    pickStart.value = ds
  } else {
    pickEnd.value = ds
  }
}

function handleConfirm() {
  if (!canConfirm.value) return
  const end = pickEnd.value || pickStart.value
  emit('confirm', [pickStart.value, end])
  emit('update:show', false)
}

function handleCancel() {
  emit('cancel')
  emit('update:show', false)
}
</script>

<style scoped lang="scss">
.pc-drp {
  position: fixed;
  inset: 0;
  z-index: 2999;
}

.pc-drp__mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
}

.pc-drp__panel {
  width: 560px;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 8px;
  box-shadow:
    0 8px 24px rgba(15, 37, 64, 0.12),
    0 0 1px rgba(15, 37, 64, 0.08);
  padding: 16px 16px 16px;
  position: fixed;
  z-index: 3000;
  box-sizing: border-box;
}

.pc-drp__arrow {
  position: absolute;
  top: -6px;
  left: var(--pc-drp-arrow-center-x, 50%);
  width: 10px;
  height: 10px;
  background: #fff;
  border-left: 1px solid #e4eaf2;
  border-top: 1px solid #e4eaf2;
  transform: translateX(-50%) rotate(45deg);
  z-index: 1;
  pointer-events: none;
}

.pc-drp__months {
  display: flex;
  gap: 0;
  align-items: stretch;
}

.pc-drp__divider {
  width: 1px;
  margin: 0 12px;
  background: #e8ecf0;
  align-self: stretch;
  min-height: 260px;
}

.pc-drp__col {
  flex: 1;
  min-width: 0;
}

.pc-drp__head {
  display: grid;
  align-items: center;
  margin-bottom: 10px;
  min-height: 32px;

  &--left {
    grid-template-columns: 28px 28px 1fr;
    gap: 4px;
  }

  &--right {
    grid-template-columns: 1fr 28px 28px;
    gap: 4px;
  }
}

.pc-drp__title {
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.pc-drp__nav {
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #3478f6;
    background: #f5f8fc;
  }
}

.pc-drp__weeks {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}

.pc-drp__weekday {
  text-align: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 28px;
}

.pc-drp__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.pc-drp__day {
  aspect-ratio: 1;
  max-height: 36px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
  cursor: pointer;

  &.is-out {
    color: rgba(0, 0, 0, 0.22);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &.is-today:not(.is-start):not(.is-end) {
    color: #3478f6;
    font-weight: 600;
  }

  &.in-range {
    background: rgba(52, 120, 246, 0.12);
  }

  &.is-start,
  &.is-end {
    background: #3478f6;
    color: #fff;
    font-weight: 600;
  }

  &:hover:not(:disabled):not(.is-start):not(.is-end) {
    background: #f0f5ff;
  }
}

.pc-drp__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.pc-drp__btn {
  min-width: 72px;
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: rgba(0, 0, 0, 0.88);

  &--ghost:hover {
    border-color: #3478f6;
    color: #3478f6;
  }

  &--primary {
    border-color: #3478f6;
    background: #3478f6;
    color: #fff;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      background: #5a9fff;
      border-color: #5a9fff;
    }
  }
}
</style>
